import React, { useState, useRef, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { FaEllipsisH, FaChevronDown, FaChevronUp, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import RecallCheck from '../RecallCheck';
import { getContrastColor } from '../../lib/colorContrast';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

const renderFormattedText = (text) => {
  if (!text) return null;

  const lines = text.split('\n');
  return lines.map((line, lineIndex) => {
    const lineKey = `line-${lineIndex}`;
    let content;

    if (line.startsWith('• ')) {
      content = (
        <div key={lineKey} className="flex">
          <span className="w-3 flex-shrink-0">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    } else if (line.startsWith('    ')) {
      content = (
        <div key={lineKey} className="pl-3">
          {line.trim()}
        </div>
      );
    } else {
      content = (
        <div key={lineKey}>
          {line}
        </div>
      );
    }

    return content;
  });
};

const TopicItem = ({ topic, displayNumber, onRemove, onEdit, onMove, onSaveRelation }) => {
  const navigate = useNavigate();
  const { isPremium, isLoading } = usePremium();
  const topicLocked = isLocked(topic, isPremium, isLoading);
  const [expanded, setExpanded] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditingRelation, setIsEditingRelation] = useState(false);
  const [relationDraft, setRelationDraft] = useState('');
  const [isSavingRelation, setIsSavingRelation] = useState(false);
  const menuRef = useRef(null);
  const relationInputRef = useRef(null);

  const toggleExpansion = () => setExpanded(!expanded);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const handleReveal = () => setIsRevealed(true);
  const handleHide = () => setIsRevealed(false);

  const handleStartEditRelation = () => {
    if (topicLocked) return;
    setRelationDraft(topic.relation_description || '');
    setIsEditingRelation(true);
  };

  const handleCancelEditRelation = () => {
    setIsEditingRelation(false);
    setRelationDraft('');
  };

  const handleSaveRelation = async () => {
    const trimmed = relationDraft.trim();
    if (!trimmed || isSavingRelation) return;

    setIsSavingRelation(true);
    try {
      const success = await onSaveRelation?.({ ...topic, relation_description: trimmed });
      if (success !== false) {
        setIsEditingRelation(false);
        setRelationDraft('');
      }
    } finally {
      setIsSavingRelation(false);
    }
  };

  const handleRelationKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditRelation();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSaveRelation();
    }
  };

  useEffect(() => {
    if (isEditingRelation && relationInputRef.current) {
      const input = relationInputRef.current;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [isEditingRelation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Briefly pulse the card when the topic newly becomes ripe.
  const prevRipeRef = useRef(topic?.is_ripe);
  const [ripePulse, setRipePulse] = useState(false);

  useEffect(() => {
    if (topic?.is_ripe && !prevRipeRef.current) {
      setRipePulse(true);
      const timer = setTimeout(() => setRipePulse(false), 500);
      prevRipeRef.current = topic?.is_ripe;
      return () => clearTimeout(timer);
    }
    prevRipeRef.current = topic?.is_ripe;
    return undefined;
  }, [topic?.is_ripe]);

  if (!topic) {
    return null;
  }

  // Find the highlighted concept and other concepts
  const highlightedConcept = topic.concepts?.find(concept => concept.is_highlighted);
  const otherConcepts = topic.concepts?.filter(concept => !concept.is_highlighted) || [];

  const handleTopicClick = (e) => {
    // Prevent redirection if clicking on menu, expand/collapse, or relationship editor
    if (
      e.target.closest('.topic-menu') ||
      e.target.closest('.expand-button') ||
      e.target.closest('.relation-edit') ||
      e.target.closest('.reveal-region')
    ) {
      return;
    }
    navigate(`/topic-detail/${topic.id}`, { 
      state: { 
        topic, 
        basket: { 
          id: topic.basket_id, 
          name: topic.basket?.name || 'Unknown Basket'
        },
        fromBasket: true,
        topicChanged: false
      } 
    });
  };

  const tagColor = topic?.tag?.color || topic?.tags?.color || '#981F2B';

  return (
    <div 
      className={`
        mb-3 rounded-xl p-3 sm:p-4 
        cursor-pointer transition duration-200 relative group
        ${topic.is_ripe 
          ? 'bg-butter-50 border-2 border-[#C9982E]' 
          : 'bg-white border border-neutral-900 hover:shadow-lg'
        }
        ${ripePulse ? 'recall-card-pop' : ''}
      `}
      onClick={handleTopicClick}
    >
      {/* Topic header with title + options menu */}
      <div className="flex justify-between items-start mb-2">
        <h3
          className="text-base sm:text-lg font-semibold text-neutral-900 line-clamp-2 flex-1 flex items-start gap-2"
          aria-label={displayNumber != null ? `${displayNumber}. ${topic.name}` : topic.name}
        >
          {displayNumber != null && (
            <span className="text-neutral-500 tabular-nums flex-shrink-0 font-medium">
              {displayNumber}.
            </span>
          )}
          <span className="min-w-0">{topic.name}</span>
        </h3>
        <div className="relative topic-menu ml-2 flex-shrink-0" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu();
            }}
            className="-mr-2 -mt-2 flex items-center justify-center p-3 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-300"
            aria-label="Topic options"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <FaEllipsisH size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-neutral-900 shadow-lg z-10 text-sm overflow-hidden">
              <button 
                onClick={(e) => { e.stopPropagation(); onEdit(topic); }} 
                className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900"
              >
                Edit
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onMove(topic); }} 
                className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900"
              >
                Move to another basket
              </button>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onEdit({ ...topic, showRelationTab: true }); 
                }} 
                className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900 flex items-center justify-between"
              >
                <span>{topic.relation_description ? 'Edit Relationship' : 'Add Relationship'}</span>
                {topic.relation_description && (
                  <span className="text-xs text-neutral-500 truncate ml-2 max-w-[100px]">
                    {topic.relation_description}
                  </span>
                )}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onRemove(topic.id); }} 
                className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-cranberry-700"
              >
                Remove from basket
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tag */}
      {(topic.tag || topic.tags) && (
        <div className="mb-3">
          <span 
            className="inline-block px-2 sm:px-3 py-0.5 rounded-full text-xs border border-neutral-900 max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis"
            style={{ 
              backgroundColor: `${tagColor}CC`,
              color: getContrastColor(tagColor)
            }}
          >
            {(topic.tag?.name || topic.tags?.name || 'No Tag')}
          </span>
        </div>
      )}

      {/* Tap-to-reveal prompt shown while the item's details are hidden */}
      {!isRevealed && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleReveal(); }}
          className="reveal-region w-full mt-2 flex items-center justify-center gap-2 transition-colors duration-200 bg-transparent hover:bg-neutral-50 rounded-lg border border-dashed border-neutral-200 px-3 py-2 text-xs text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
          aria-label={`Reveal details and recall check for ${topic.name}`}
          aria-expanded={false}
        >
          <FaEye size={12} aria-hidden="true" />
          Tap to reveal why it&apos;s here
        </button>
      )}

      {isRevealed && (
        <>
      {/* Relationship description - inline editable */}
      <div className="relation-edit mb-3" onClick={(e) => e.stopPropagation()}>
        {isEditingRelation ? (
          <div className="space-y-2">
            <textarea
              ref={relationInputRef}
              value={relationDraft}
              onChange={(e) => setRelationDraft(e.target.value)}
              onKeyDown={handleRelationKeyDown}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cranberry-700 min-h-[80px]"
              placeholder="Describe how this topic relates to the basket or other items in it..."
              aria-label="Relationship description"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelEditRelation}
                className="px-3 py-1 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 text-neutral-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRelation}
                disabled={!relationDraft.trim() || isSavingRelation}
                className={`
                  px-3 py-1 text-sm rounded-lg text-white
                  ${!relationDraft.trim() || isSavingRelation
                    ? 'bg-cranberry-300 cursor-not-allowed'
                    : 'bg-cranberry-700 hover:bg-cranberry-600'
                  }
                `}
              >
                {isSavingRelation ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : topicLocked ? (
          <p className="text-sm text-neutral-500 inline-flex items-center gap-1.5">
            <Lock size={13} aria-hidden="true" />
            Connection details are Premium
          </p>
        ) : topic.relation_description ? (
          <p
            role="button"
            tabIndex={0}
            onClick={handleStartEditRelation}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStartEditRelation();
              }
            }}
            className="text-sm italic text-neutral-600 rounded-lg px-2 py-1 -mx-2 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-cranberry-700 cursor-text"
            aria-label="Edit relationship description"
          >
            &ldquo;{topic.relation_description}&rdquo;
          </p>
        ) : (
          <button
            type="button"
            onClick={handleStartEditRelation}
            className="text-sm text-neutral-400 hover:text-cranberry-700 focus:outline-none focus:ring-2 focus:ring-cranberry-700 rounded-lg px-1 -mx-1"
            aria-label="Add relationship description"
          >
            + Add relationship
          </button>
        )}
      </div>
      
      <div className="mt-2">
        {highlightedConcept && (
          <div className="relative pl-3 py-1 mb-2">
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${topic.is_ripe ? 'bg-[#C9982E]' : 'bg-cranberry-400'}`}></div>
            <div className="text-blue-700 text-[13px] sm:text-sm line-clamp-3 sm:line-clamp-none font-normal">
              {renderFormattedText(highlightedConcept.text)}
            </div>
          </div>
        )}

        {otherConcepts.length > 0 && expanded && (
          <ul className="space-y-1 sm:space-y-2 mt-2">
            {otherConcepts.map((concept, index) => (
              <li key={index} className="text-blue-700 text-[13px] sm:text-sm flex font-normal">
                <span className="w-3 flex-shrink-0">•</span>
                <span className="line-clamp-2 sm:line-clamp-none">
                  {renderFormattedText(concept.text)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Controls row: expand toggle */}
      {otherConcepts.length > 0 && (
        <div className="flex items-center mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpansion();
            }}
            className="expand-button -ml-2 flex items-center justify-center p-3 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-300"
            aria-label={expanded ? "Collapse concepts" : "Expand concepts"}
            aria-expanded={expanded}
          >
            {expanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </button>
        </div>
      )}

      {/* Recall ripeness section */}
      {!topicLocked && (
        <div onClick={(e) => e.stopPropagation()}>
          <RecallCheck
            topicId={topic.id}
            topicName={topic.name}
          />
        </div>
      )}

      {/* Hide toggle - collapses the revealed details back down */}
      <div className="reveal-region flex justify-center mt-3">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleHide(); }}
          className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-neutral-300 rounded-lg px-2 py-1"
          aria-label={`Hide details and recall check for ${topic.name}`}
          aria-expanded={true}
        >
          <FaEyeSlash size={13} aria-hidden="true" />
          Hide
        </button>
      </div>
        </>
      )}
    </div>
  );
};

export default TopicItem;
